'use client';

import axios from 'axios';
import React from 'react';

import isBodyAllowed from 'lib/api/isBodyAllowed';
import type { ResourceError, ResourcePath } from 'lib/api/resources';

export interface Params {
  method?: RequestInit['method'];
  headers?: RequestInit['headers'];
  signal?: RequestInit['signal'];
  body?: Record<string, unknown> | FormData;
  credentials?: RequestCredentials;
}

interface Meta {
  resource?: ResourcePath;
  logError?: boolean;
}

export default function useFetch() {
  return React.useCallback(async <Success, Error>(path: string, params?: Params, meta?: Meta): Promise<Success | ResourceError<Error>> => {
    const _body = params?.body;
    const isFormData = _body instanceof FormData;
    const withBody = isBodyAllowed(params?.method);

    const data = (() => {
      if (!withBody) return undefined;
      return _body;
    })();

    const headers: Record<string, string> = {
      ...(withBody && !isFormData ? { 'Content-type': 'application/json' } : undefined),
      ...(params?.headers as Record<string, string>),
    };

    try {
      const response = await axios({
        url: path,
        method: (params?.method || 'GET') as string,
        headers,
        data,
        signal: params?.signal,
        withCredentials: params?.credentials === 'include',
      });

      return response.data as Success;
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        const { status, statusText, data: payload } = err.response;
        return Promise.reject({ payload, status, statusText });
      }
      return Promise.reject({ payload: err, status: 0, statusText: 'Network Error' });
    }
  }, []);
}
